import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { showErrorAlert, showSuccessAlert } from "../../../components/alerts";
import medicGeekApi from "../../../config/axiosConfig";

const LabTestManagement = () => {
  const [tests, setTests] = useState([]);
  const [showForm] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const testsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [formData, setFormData] = useState({ name: "", status: false });
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' }); // Estado para la configuración de ordenación
 // Skeleton
 const [loading, setLoading] = useState(true);
 const [hasLoaded, setHasLoaded] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Iniciar la carga
      try {
        const response = await medicGeekApi.get("/labtestparam", {
          params: {
            search: searchTerm,
          },
        });

        // Ordenar los datos según la configuración de ordenación
        let sortedTests = [...response.data];
        if (sortConfig.key) {
          sortedTests.sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }

        setTests(sortedTests);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false); // Finalizar carga
      }
    };

    fetchData();
  }, [searchTerm, sortConfig]);


  const handleShowModal = (test = null) => {
    if (test) {
      setIsEditing(true);
      setCurrentTest(test);
      setFormData({ name: test.AnalisisNm || "", status: test.estatus === "Activo" });
    } else {
      setIsEditing(false);
      setCurrentTest(null);
      setFormData({ name: "", status: false });
    }
    setShowModal(true);
  };

  const handleupdate = () => {
    medicGeekApi
      .get("/labtestparam")
      .then((response) => {
        setTests(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSearchTermChange = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);
  };

  const pageCount = Math.ceil(tests.length / testsPerPage);

  const handlePageChange = ({ selected }) => {
    setPageNumber(selected);
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      // Si el input está vacío, muestra una alerta de error
      showErrorAlert("El nombre del análisis no puede estar vacío");
      return;
    }
  
    // Verificar si ya existe un análisis con el mismo nombre
    medicGeekApi
      .get(`/labtestparam?name=${encodeURIComponent(formData.name)}`)
      .then((response) => {
        // Filtrar para coincidencia exacta
        const analisisExistente = response.data.find(
          (analisis) => analisis.AnalisisNm.trim().toLowerCase() === formData.name.trim().toLowerCase()
        );
  
        if (analisisExistente) {
          // Si el análisis ya existe, mostrar alerta de error
          showErrorAlert("El nombre del análisis ya existe");
        } else {
          // Si no existe, proceder a guardar el nuevo análisis
          const nuevoClassification = {
            AnalisisNm: formData.name,
            estatus: "Activo",
          };
  
          medicGeekApi
            .post("/labtestparam", nuevoClassification)
            .then((response) => {
              showSuccessAlert();
              setShowModal(false);
              // Recargar la lista de análisis
              handleupdate();
              // Limpiar el input después de añadir
              setFormData({ name: "", status: true });
            })
            .catch((error) => {
              console.error("Error al crear el análisis", error);
              showErrorAlert();
            });
        }
      })
      .catch((error) => {
        console.error("Error al verificar el nombre del análisis", error);
        showErrorAlert("Hubo un error al verificar el nombre del análisis");
      });
  };
  
  const handleEdit = (id) => {
    const updatedClassification = {
      AnalisisNm: formData.name,
      estatus: formData.status ? "Activo" : "Inactivo",
    };

    medicGeekApi
      .patch(`/labtestparam/${id}`, updatedClassification)
      .then((response) => {
        showSuccessAlert();
        setShowModal(false);
        // Recargar la lista de análisis
        handleupdate();
      })
      .catch((error) => {
        console.error("Error al editar el análisis", error);
        showErrorAlert();
      });
  };

  const handleDelete = (id) => {
    medicGeekApi
      .delete(`/labtestparam/${id}`)
      .then(() => {
        setTests(tests.filter((test) => test.labTestParamId !== id));
      })
      .catch((error) => console.error("Error deleting data:", error));
  };

  const handleStatusToggle = (id, currentStatus) => {
    const updatedStatus = currentStatus === "Activo" ? "Inactivo" : "Activo";

    medicGeekApi
      .patch(`/labtestparam/${id}`, { estatus: updatedStatus })
      .then(() => {
        setTests(
          tests.map((test) =>
            test.labTestParamId === id
              ? { ...test, estatus: updatedStatus }
              : test
          )
        );
      })
      .catch((error) => {
        console.error("Error al cambiar el estatus:", error);
      });
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const direction = (prevConfig.key === key && prevConfig.direction === 'asc') ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const filteredTests = tests.filter((test) =>
    (test.AnalisisNm || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedTests = filteredTests.slice(
    pageNumber * testsPerPage,
    (pageNumber + 1) * testsPerPage
  );

  const SkeletonTableRow = () => (
    <tr>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "150px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "70px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "200px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}></div>
      </td>
    </tr>
  );

  return (
  <div className="pro-container">

    {/* ===================================================== */}
    {/* HEADER */}
    {/* ===================================================== */}

    <div className="pro-header">

      <div className="pro-header-left">

        <h2 className="pro-title">
          Mantenimiento Prueba de Laboratorio
        </h2>

        <p className="pro-subtitle">
          Gestión y mantenimiento de análisis de laboratorio
        </p>

      </div>

      <div className="pro-actions">

        <div className="pro-search">

          <i className="bi bi-search"></i>

          <input
            type="search"
            placeholder="Buscar análisis..."
            value={searchTerm}
            onChange={handleSearchTermChange}
          />

        </div>

      </div>

    </div>

    {/* ===================================================== */}
    {/* QUICK CREATE */}
    {/* ===================================================== */}

    <div className="pro-card mb-4">

      <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">

        <div className="flex-grow-1">

          <label className="form-label fw-semibold mb-2">
            Nombre del Análisis
          </label>

          <input
            type="text"
            className="form-control pro-input m-2"
            placeholder="Ingrese el nombre del análisis"
            id="nombreAnalisis"
            value={formData.name}
            onChange={(e) => {
              setFormData({
                ...formData,
                name: e.target.value,
              });

              setErrorMessage(null);
            }}
          />

        </div>

        <div className="d-flex align-items-end pt-4">

          <button
            className="pro-btn-primary"
            onClick={() => handleSave()}
          >
            <i className="bi bi-plus-circle"></i>
            Añadir Análisis
          </button>

        </div>

      </div>

    </div>

    {/* ===================================================== */}
    {/* TABLE DESKTOP */}
    {/* ===================================================== */}

    <div className="pro-card d-none d-md-block">

      <div className="table-responsive">

        <table className="table pro-table">

          <thead>

            <tr>

              <th
                style={{ width: "10%", cursor: "pointer" }}
                onClick={() => handleSort("labTestParamId")}
              >
                ID{" "}
                {sortConfig.key === "labTestParamId"
                  ? sortConfig.direction === "asc"
                    ? "🔼"
                    : "🔽"
                  : null}
              </th>

              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("AnalisisNm")}
              >
                Nombre del Análisis{" "}
                {sortConfig.key === "AnalisisNm"
                  ? sortConfig.direction === "asc"
                    ? "🔼"
                    : "🔽"
                  : null}
              </th>

              <th
                style={{ width: "15%", cursor: "pointer" }}
                onClick={() => handleSort("estatus")}
              >
                Estado{" "}
                {sortConfig.key === "estatus"
                  ? sortConfig.direction === "asc"
                    ? "🔼"
                    : "🔽"
                  : null}
              </th>

              <th style={{ width: "15%" }}>
                Acciones
              </th>

            </tr>

          </thead>

          <tbody>

            {loading ? (

              Array.from({ length: 15 }).map((_, index) => (
                <SkeletonTableRow key={index} />
              ))

            ) : (

              paginatedTests.map((test) => (

                <tr key={test.labTestParamId}>

                  <td>
                    #{test.labTestParamId}
                  </td>

                  <td className="fw-semibold">
                    {test.AnalisisNm || "N/A"}
                  </td>

                  <td>

                    <span
                      className={`expediente-badge ${
                        test.estatus === "Activo"
                          ? ""
                          : "gender-badge"
                      }`}
                    >
                      {test.estatus}
                    </span>

                  </td>

                  <td>

                    <div className="pro-table-actions">

                      <button
                        type="button"
                        className="pro-action-btn success"
                        title="Cambiar Estado"
                        onClick={() =>
                          handleStatusToggle(
                            test.labTestParamId,
                            test.estatus
                          )
                        }
                      >
                        <i className="bi bi-toggle-on"></i>
                      </button>

                      <button
                        type="button"
                        className="pro-action-btn primary"
                        title="Editar"
                        onClick={() =>
                          handleShowModal(test)
                        }
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>

                      <button
                        type="button"
                        className="pro-action-btn warning"
                        title="Eliminar"
                        onClick={() =>
                          handleDelete(test.labTestParamId)
                        }
                      >
                        <i className="bi bi-trash"></i>
                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>

    {/* ===================================================== */}
    {/* MOBILE CARDS */}
    {/* ===================================================== */}

    <div className="d-md-none">

      <div className="row">

        {loading ? (

          Array.from({ length: 5 }).map((_, index) => (

            <div className="col-12 mb-3" key={index}>

              <div className="mobile-patient-card">

                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line"></div>

              </div>

            </div>

          ))

        ) : (

          paginatedTests.map((test) => (

            <div
              className="col-12 mb-3"
              key={test.labTestParamId}
            >

              <div className="mobile-patient-card">

                <div className="mobile-card-top">

                  <div className="patient-info">

                    <div className="patient-avatar">
                      {test.AnalisisNm?.charAt(0)}
                    </div>

                    <div>

                      <div className="patient-name">
                        {test.AnalisisNm}
                      </div>

                      <div className="patient-id">
                        Análisis #{test.labTestParamId}
                      </div>

                    </div>

                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Estado
                  </small>

                  <span
                    className={`expediente-badge ${
                      test.estatus === "Activo"
                        ? ""
                        : "gender-badge"
                    }`}
                  >
                    {test.estatus}
                  </span>

                </div>

                <div className="mobile-card-actions">

                  <button
                    type="button"
                    className="pro-action-btn success"
                    onClick={() =>
                      handleStatusToggle(
                        test.labTestParamId,
                        test.estatus
                      )
                    }
                  >
                    <i className="bi bi-toggle-on"></i>
                  </button>

                  <button
                    type="button"
                    className="pro-action-btn primary"
                    onClick={() =>
                      handleShowModal(test)
                    }
                  >
                    <i className="bi bi-pencil-square"></i>
                  </button>

                  <button
                    type="button"
                    className="pro-action-btn warning"
                    onClick={() =>
                      handleDelete(test.labTestParamId)
                    }
                  >
                    <i className="bi bi-trash"></i>
                  </button>

                </div>

              </div>

            </div>

          ))

        )}

      </div>

    </div>

    {/* ===================================================== */}
    {/* PAGINATION */}
    {/* ===================================================== */}

    {!showForm && (

      <div className="pro-pagination">

        <nav aria-label="Page navigation example">

          <ul className="pagination modern-pagination">

            {/* PREVIOUS */}

            <li
              className={`page-item ${
                pageNumber === 0
                  ? "disabled"
                  : ""
              }`}
            >

              <span
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber - 1,
                  })
                }
                style={{
                  cursor:
                    pageNumber === 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <i className="bi bi-chevron-left"></i>
              </span>

            </li>

            {/* NUMBERS */}

            {Array.from(
              { length: Math.min(pageCount, 10) },
              (_, index) => (

                <li
                  key={index}
                  className={`page-item ${
                    pageNumber === index
                      ? "active"
                      : ""
                  }`}
                >

                  <span
                    className="page-link"
                    onClick={() =>
                      handlePageChange({
                        selected: index,
                      })
                    }
                  >
                    {index + 1}
                  </span>

                </li>

              )
            )}

            {/* NEXT */}

            <li
              className={`page-item ${
                pageNumber === pageCount - 1
                  ? "disabled"
                  : ""
              }`}
            >

              <span
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber + 1,
                  })
                }
                style={{
                  cursor:
                    pageNumber ===
                    pageCount - 1
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <i className="bi bi-chevron-right"></i>
              </span>

            </li>

          </ul>

        </nav>

      </div>

    )}

    {/* ===================================================== */}
    {/* MODAL */}
    {/* ===================================================== */}

    <Modal
      isOpen={showModal}
      toggle={() => setShowModal(false)}
      centered
      className="pro-modal"
    >

      <ModalHeader
        toggle={() => setShowModal(false)}
      >

        {isEditing
          ? "Editar Análisis"
          : "Añadir Análisis"}

      </ModalHeader>

      <ModalBody>

        <Form>

          <FormGroup className="mb-3">

            <Label for="labTestParamId">
              ID
            </Label>

            <Input
              id="labTestParamId"
              type="text"
              className="pro-input"
              value={
                currentTest
                  ? currentTest.labTestParamId
                  : ""
              }
              disabled
            />

          </FormGroup>

          <FormGroup className="mb-3">

            <Label for="nombreAnalisis">
              Nombre del Análisis
            </Label>

            <Input
              id="nombreAnalisis"
              type="text"
              className="pro-input"
              value={formData.name}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  name: e.target.value,
                })
              }
            />

          </FormGroup>

          <FormGroup>

            <Label
              for="estatusAnalisis"
              className="mb-2"
            >
              Estado
            </Label>

            <div>

              <Input
                id="estatusAnalisis"
                type="checkbox"
                checked={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.checked,
                  })
                }
              />

            </div>

          </FormGroup>

        </Form>

      </ModalBody>

      <ModalFooter>

        <Button
          className="pro-btn-cancel"
          onClick={() =>
            setShowModal(false)
          }
        >
          Cancelar
        </Button>

        <Button
          className="pro-btn-save"
          onClick={
            isEditing
              ? () =>
                  handleEdit(
                    currentTest.labTestParamId
                  )
              : handleSave
          }
        >
          {isEditing
            ? "Guardar Cambios"
            : "Añadir Análisis"}
        </Button>

      </ModalFooter>

    </Modal>

  </div>
);
};

export default LabTestManagement;
